// ============================================================
// Modèle de données du POC — aligné sur la prod réelle
// ============================================================
//
// Architecture Paladium Bedrock / Recube :
//
// - Côté Minecraft, chaque type de serveur = un **Container Pterodactyl**
//   (Dockerfile + release-version.json). Les fichiers vivent dans
//   Paladium-Bedrock/PaladiumBE-<Type>-Container.
//   Chaque container a une VERSION (ex: v0.0.122), et déclare une liste
//   d'artefacts (mods/modules) téléchargés depuis GitHub Releases.
//
// - Deux modes de gestion :
//    * 'individual'    — les serveurs sont gérés un par un sur Pterodactyl
//                        (ex: Faction, Proxy, Build)
//    * 'nexus-dynamic' — Nexus spawn/despawn des instances selon la demande
//                        (ex: Lobby, Farmland, Mining, Warzone)
//
// - Côté API : ~14 services qui tournent actuellement dans des screens sur
//   une vieille machine, bientôt sur Pterodactyl eux aussi.
//
// - Resource packs : à part (livrés aux clients Bedrock).
// ============================================================

export type EnvironmentId = 'dev' | 'beta' | 'preprod' | 'prod';

export type DeploymentStatus =
  | 'idle'
  | 'queued'
  | 'running'
  | 'success'
  | 'failed'
  | 'cancelled';

export type ServiceHealth = 'healthy' | 'degraded' | 'down' | 'restarting' | 'detached';

// Rôle du service dans l'écosystème
export type ServiceFamily = 'minecraft-container' | 'api';

// Mode de gestion d'un type Minecraft
export type ManagementMode = 'individual' | 'nexus-dynamic';

// Runtime d'une API (actuel vs cible)
export type ApiRuntime = 'screen' | 'pterodactyl';

// Destination dans le container MC (dossier de drop par le ReleaseDownloader)
export type ArtifactDest =
  | '/mcserv/mods/'
  | '/mcserv/modules/'
  | '/mcserv/plugins/'
  | '/mcserv/configs/'
  | '/mcserv/libs/';

export type ArtifactKind = 'mod' | 'module' | 'plugin' | 'config' | 'lib';

export type Permission =
  | 'cicd.view'
  | 'cicd.deploy.beta'
  | 'cicd.deploy.preprod'
  | 'cicd.deploy.prod'
  | 'cicd.restart'
  | 'cicd.config.dotnet'
  | 'cicd.resourcepack.release'
  | 'cicd.schedule'
  | 'cicd.logs'
  | 'env.dev.manage'
  | 'env.beta.promote'
  | 'cicd.network';

export type Role = 'developer' | 'admin' | 'project-manager' | 'viewer';

export interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  avatarColor: string;
  permissions: Permission[];
  maxDevEnvs?: number; // limite d'envs de dev (0 = aucun, undefined = illimité)
}

export interface Project {
  id: string;
  name: string;
  code: string;
  description: string;
  color: string;
  dotnetVersion: string;
  createdAt: string;
  /** UUID v4 stable, utilisé par l'endpoint public /api/public/{token}/apis. */
  publicApiToken?: string;
  /** Groupes de jeu (Nova, Iris...). Absent sur un CICD plus ancien : aucun groupe. */
  groups?: ProjectGroup[];
  /**
   * Groupes de jeu, instances d'API et répartition ouverts à ce projet (NationsGlory seulement).
   * Tout est masqué sauf s'il vaut true : absent (CICD plus ancien) = masqué.
   */
  gameGroupsEnabled?: boolean;
}

/**
 * Groupe de jeu d'un projet : sous-ensemble de serveurs de jeu déclaré par la variable
 * Pterodactyl RECUBE_GROUP. `key` est stable (paramètre public ?group=, répartition des APIs).
 */
export interface ProjectGroup {
  key: string;
  label: string;
  /** "#RRGGBB", optionnelle. */
  color?: string;
}

// --- Artefact configuré dans un release-version.json ---
export interface ArtifactRelease {
  name: string;             // ex: "PalaMod"
  repository: string;       // ex: "Paladium-Bedrock/PalaMod"
  version: string;          // ex: "0.0.34"
  dest: ArtifactDest;       // dossier cible
  assetName: string;        // ex: "PalaMod.dll"
  kind: ArtifactKind;       // déduit de dest
  // Dernière release disponible sur GitHub (peut être > à version)
  latestAvailable?: string;
}

// --- Un type de container Minecraft (egg Pterodactyl) ---
export interface MinecraftContainer {
  id: string;                  // ex: "c-faction"
  projectId: string;
  code: string;                // ex: "faction" (matche nom dans Paladium-Bedrock/...-Container)
  displayName: string;         // ex: "Faction"
  color: string;
  repository: string;          // ex: "Paladium-Bedrock/PaladiumBE-Faction-Container"
  containerVersion: string;    // ex: "v0.0.122"
  managementMode: ManagementMode;
  dotnetVersion: string;       // embarqué dans Dockerfile
  /**
   * Template d'image Docker publiée par le CI du repo.
   * Tokens : {version} = tag sans 'v' (ex 0.0.123), {versionRaw} = avec 'v' (ex v0.0.123)
   * Ex avec convention : "harbor.be.recube.gg/paladium-bedrock/proxy:{version}"
   * Ex statique :        "harbor.be.recube.gg/paladium-bedrock/minage:latest"
   * Si pas de token de version → image statique (update-images saute ce container).
   * Optional côté types pour rester compat avec MOCK_CONTAINERS — toujours présent côté API.
   */
  dockerImage?: string;
  releases: ArtifactRelease[]; // contenu du release-version.json
  lastBuildAt: string;
  // Version déployée par env (tag docker courant)
  deployedVersions: Record<EnvironmentId, string | null>;
  // Nombre de commits/bumps en attente par env
  pendingBumps: Record<EnvironmentId, number>;
  /**
   * Container archivé : absent de la liste par défaut, plus synchronisé, plus proposé aux releases
   * ni aux promotions, fichiers en lecture seule ; son historique est gardé et « Désarchiver » le
   * remet tel quel. Absent (CICD d'avant l'archivage) = actif.
   */
  archived?: boolean;
  archivedAt?: string | null;
  archivedByName?: string | null;
}

// --- Une instance Minecraft en cours d'exécution ---
export interface MinecraftInstance {
  id: string;
  projectId: string;
  containerId: string;         // lien -> MinecraftContainer.id
  environment: EnvironmentId;
  name: string;                // ex: "faction-pvp-1"
  runningVersion: string;      // ex: "v0.0.120" (peut diverger du container)
  health: ServiceHealth;
  uptimeSec: number;
  players?: number;
  node: string;                // ex: "prod-eu-1"
  pteroServerId: string;       // ex: "a1b2c3d4"
  isNexusManaged: boolean;
  lastHeartbeatAt: string;
}

// --- Un service d'API ---
export interface ApiService {
  id: string;
  projectId: string;
  name: string;                // ex: "ModerationAPI"
  pidOrScreen: string;         // ex: "2108120.ModerationAPI"
  runtime: ApiRuntime;
  targetRuntime: ApiRuntime;   // objectif (tous passeront en ptero)
  health: ServiceHealth;
  version: string;             // version en cours (déployée)
  latestAvailable?: string;    // dernière release dispo (si différente de version)
  lastDeployAt: string;
  environment: EnvironmentId;
  node?: string;
  uptimeSec: number;
}

// --- Resource pack ---
// Un pack = une asset .zip versionnée (ex: PalaResourcePack.zip).
// Même pattern que les APIs : Prod vs Dev, avec latestAvailable pour signaler
// les packs en retard.
export interface ResourcePack {
  id: string;
  projectId: string;
  name: string;                  // ex: "Pala"
  fileName: string;              // ex: "PalaResourcePack.zip"
  description?: string;
  repository: string;            // ex: "Paladium-Bedrock/PalaResourcePack"
  currentVersion: string;        // version déployée
  latestAvailable?: string;      // dernière release prête si différente
  activeIn: EnvironmentId[];     // envs où le pack est actif
  lastUpdateAt: string;
  lastUpdateBy: string;
  commitMessage?: string;        // dernier commit pour l'aperçu
  sizeMb?: number;
}

export interface DevResourcePack {
  id: string;
  projectId: string;
  name: string;
  fileName: string;
  description?: string;
  repository: string;
  version: string;
  status: 'building' | 'ready' | 'error';
  scope: 'shared' | 'env-specific';
  attachedEnvIds: string[];
  createdAt: string;
  createdBy: string;
  createdByName: string;
  createdByColor?: string;
  sizeMb?: number;
}

// --- Déploiement ---
export type DeployTargetKind = 'container' | 'artifact' | 'api' | 'resource-pack';

export interface DeploymentStep {
  id: string;
  label: string;
  status: DeploymentStatus;
  startedAt?: string;
  finishedAt?: string;
  logs?: string[];
}

export interface Deployment {
  id: string;
  projectId: string;
  source: EnvironmentId;
  target: EnvironmentId;
  kind: DeployTargetKind;
  // Ce qui est embarqué (IDs de containers, ou d'artefacts "container:artifact", etc.)
  items: string[];
  includedServices: EnvironmentId[];
  status: DeploymentStatus;
  author: User;
  startedAt: string;
  finishedAt?: string;
  durationSec?: number;
  steps: DeploymentStep[];
  restartAfter: boolean;
  scheduledAt?: string;
  note?: string;
}

export interface EnvironmentMeta {
  id: EnvironmentId;
  name: string;
  description: string;
  color: string;
  order: number;
  requiresConfirmation: boolean;
}

export const ENVIRONMENTS: EnvironmentMeta[] = [
  { id: 'dev',     name: 'Dev',      description: 'Environnement de travail des développeurs', color: 'var(--color-env-dev)',     order: 0, requiresConfirmation: false },
  { id: 'beta',    name: 'Bêta',     description: 'Environnement de test',                      color: 'var(--color-env-beta)',    order: 1, requiresConfirmation: false },
  { id: 'preprod', name: 'Pré-prod', description: 'Validation finale & configuration',          color: 'var(--color-env-preprod)', order: 2, requiresConfirmation: true  },
  { id: 'prod',    name: 'Prod',     description: 'Production — utilisateurs finaux',            color: 'var(--color-env-prod)',    order: 3, requiresConfirmation: true  },
];

export const ENVIRONMENT_BY_ID: Record<EnvironmentId, EnvironmentMeta> = ENVIRONMENTS.reduce(
  (acc, env) => ({ ...acc, [env.id]: env }),
  {} as Record<EnvironmentId, EnvironmentMeta>,
);

// --- APIs en cours de développement ---
// Contrairement aux ApiService (prod), les DevApi sont soit partagées entre
// plusieurs envs de dev, soit scoped à un seul env.
export interface DevApi {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  repository: string;           // ex: "Paladium-Bedrock/EventAPI"
  version: string;              // ex: "0.3.2"
  startCommand: string;         // ex: "dotnet EventAPI.dll"
  dockerImage?: string;         // optionnel — sinon build local
  port: number;
  status: 'running' | 'stopped' | 'building' | 'error';
  scope: 'shared' | 'env-specific';
  attachedEnvIds: string[];
  createdAt: string;
  createdBy: string;
  createdByName: string;
  createdByColor?: string;
}

// --- Environnements de dev (un par dev, 1 instance MC) ---
export interface CustomApi {
  id: string;
  name: string;
  description?: string;
  port: number;
  version: string;
  status: 'running' | 'stopped';
}

export interface DevEnv {
  id: string;
  projectId: string;
  name: string;              // ex: "lucas-faction-event"
  ownerId: string;
  ownerName: string;
  ownerColor: string;
  modelCode: string;         // container code: 'faction', 'mining', etc.
  createdAt: string;
  apiMode: 'shared' | 'isolated';
  customApis: CustomApi[];
  attachedDevApiIds: string[]; // DevApi attachées à cet env
  mcInstanceId: string;
  status: 'running' | 'stopped' | 'starting' | 'archived';
}

// --- Slots Bêta A..E ---
export type BetaSlotId = 'A' | 'B' | 'C' | 'D' | 'E';
export const BETA_SLOT_IDS: BetaSlotId[] = ['A', 'B', 'C', 'D', 'E'];

export interface BetaSlot {
  id: BetaSlotId;
  projectId: string;
  allocated: boolean;
  label?: string;
  sourceDevEnvId?: string;
  sourceDevEnvName?: string;
  allocatedAt?: string;
  allocatedByUserId?: string;
  allocatedByUserName?: string;
  mcInstanceId?: string;
  modelCode?: string;
  // Snapshot capturé au moment du push — sert à calculer le diff avec l'état
  // courant de l'env source pour proposer un "Update".
  snapshotContainerVersion?: string;
  snapshotApiIds?: string[];
  snapshotAt?: string;
}

// Couleurs par type de container (palette cohérente bleutée)
export const CONTAINER_COLORS: Record<string, string> = {
  build:    '#A78BFA',
  faction:  '#F59E0B',
  farmland: '#10B981',
  lobby:    '#38BDF8',
  mining:   '#F472B6',
  proxy:    '#6366F1',
  warzone:  '#EF4444',
};

// Libellés dossier → kind
export const DEST_TO_KIND: Record<ArtifactDest, ArtifactKind> = {
  '/mcserv/mods/':     'mod',
  '/mcserv/modules/':  'module',
  '/mcserv/plugins/':  'plugin',
  '/mcserv/configs/':  'config',
  '/mcserv/libs/':     'lib',
};
